import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search, Users, X as XIcon, Plus } from 'lucide-react';
import { apiService, PatientListItem } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import PageHeader from './ui/PageHeader';
import StatusPill from './ui/StatusPill';
import EmptyState from './ui/EmptyState';

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const fmtDateDe = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

const LANG_META: Record<string, string> = { de: 'DE', en: 'EN', es: 'ES', ru: 'RU' };

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
    <th
        scope="col"
        className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
        {children}
    </th>
);

const PatientsListPage: React.FC = () => {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const [patients, setPatients] = useState<PatientListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [search, setSearch] = useState('');
    const [archived, setArchived] = useState<'false' | 'true' | ''>('false');
    const [langFilter, setLangFilter] = useState<string>('');
    const [intakeFilter, setIntakeFilter] = useState<'' | 'true' | 'false'>('');

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await apiService.listPatients({
                search: search || undefined,
                archived: archived || undefined,
                language: langFilter || undefined,
                hasIntake: intakeFilter || undefined,
                limit: 200,
            });
            setPatients(res.data.data || []);
        } catch (e: any) {
            setError(e?.response?.data?.message || t('patients.list.errorLoad'));
        } finally {
            setLoading(false);
        }
    };

    // Initial load and reload on filter changes (debounce search lightly).
    useEffect(() => {
        const t = setTimeout(load, search ? 250 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, archived, langFilter, intakeFilter]);

    const hasActiveFilters = !!(search || langFilter || intakeFilter || archived !== 'false');
    const resetFilters = () => {
        setSearch(''); setLangFilter(''); setIntakeFilter(''); setArchived('false');
    };

    const stats = useMemo(() => ({
        total: patients.length,
        withIntake: patients.filter(p => !!p.intake_form_id).length,
        archived: patients.filter(p => p.archived).length,
    }), [patients]);

    const handleCreate = async () => {
        const fullName = window.prompt(t('patients.list.promptName'));
        if (!fullName || fullName.trim().length < 2) return;
        const phone = window.prompt(t('patients.list.promptPhone')) || '';
        try {
            const res = await apiService.createPatient({ full_name: fullName.trim(), phone_primary: phone.trim() || null, source: 'admin' });
            addToast(t('patients.list.created', { number: res.data.data.patient_number }), 'success');
            load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('patients.list.errorCreate'), 'error');
        }
    };

    return (
        <div>
            <PageHeader
                eyebrow={t('patients.list.eyebrow')}
                title={t('patients.list.title')}
                description={t('patients.list.description')}
                actions={
                    <>
                        <span className="text-xs text-brand-text-muted">
                            <span className="font-semibold text-brand-text tabular-nums">{stats.total}</span> {t('patients.list.active')}
                            {stats.withIntake > 0 && <> · <span className="tabular-nums">{stats.withIntake}</span> {t('patients.list.withIntake')}</>}
                        </span>
                        <button
                            onClick={handleCreate}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer"
                        >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                            {t('patients.list.newPatient')}
                        </button>
                    </>
                }
            />

            {error && (
                <div className="mb-4 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
                    {error}
                </div>
            )}

            <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="lg:col-span-2">
                        <label className={labelClass}>{t('patients.list.searchLabel')}</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-muted pointer-events-none" strokeWidth={1.75} aria-hidden />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('patients.list.searchPlaceholder')}
                                className={`${inputClass} pl-9`}
                            />
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>{t('patients.list.intake')}</label>
                        <select value={intakeFilter} onChange={(e) => setIntakeFilter(e.target.value as any)} className={inputClass}>
                            <option value="">{t('patients.list.intakeAll')}</option>
                            <option value="true">{t('patients.list.intakeWith')}</option>
                            <option value="false">{t('patients.list.intakeWithout')}</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>{t('patients.list.language')}</label>
                        <select value={langFilter} onChange={(e) => setLangFilter(e.target.value)} className={inputClass}>
                            <option value="">{t('patients.list.langAll')}</option>
                            <option value="de">{t('patients.list.langDe')}</option>
                            <option value="en">{t('patients.list.langEn')}</option>
                            <option value="es">{t('patients.list.langEs')}</option>
                            <option value="ru">{t('patients.list.langRu')}</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>{t('patients.list.statusLabel')}</label>
                        <select value={archived} onChange={(e) => setArchived(e.target.value as any)} className={inputClass}>
                            <option value="false">{t('patients.list.statusActive')}</option>
                            <option value="true">{t('patients.list.statusArchived')}</option>
                            <option value="">{t('patients.list.statusAll')}</option>
                        </select>
                    </div>
                </div>
                {hasActiveFilters && (
                    <div className="mt-3 flex justify-end">
                        <button
                            onClick={resetFilters}
                            className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors cursor-pointer"
                        >
                            <XIcon className="h-3 w-3" strokeWidth={2} />
                            {t('patients.list.resetFilters')}
                        </button>
                    </div>
                )}
            </div>

            <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sm text-brand-text-muted">{t('patients.list.loading')}</div>
                ) : patients.length === 0 ? (
                    <EmptyState
                        icon={Users}
                        title={hasActiveFilters ? t('patients.list.emptyNoHitsTitle') : t('patients.list.emptyNoPatientsTitle')}
                        description={
                            hasActiveFilters
                                ? t('patients.list.emptyNoHitsDescription')
                                : t('patients.list.emptyNoPatientsDescription')
                        }
                    />
                ) : (
                    <>
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-brand-border">
                                <thead className="bg-brand-surface-2/60">
                                    <tr>
                                        <Th>{t('patients.list.colPatient')}</Th>
                                        <Th>{t('patients.list.colContact')}</Th>
                                        <Th>{t('patients.list.colAppointments')}</Th>
                                        <Th>{t('patients.list.colIntake')}</Th>
                                        <Th align="right">{t('patients.list.colAction')}</Th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-brand-border">
                                    {patients.map((p) => (
                                        <tr key={p.id} className="hover:bg-brand-surface-2/60 transition-colors">
                                            <td className="px-4 py-3 text-sm">
                                                <div className="font-medium text-brand-text">{p.full_name}</div>
                                                <div className="text-xs text-brand-text-muted tabular-nums">
                                                    {p.patient_number}
                                                    {p.date_of_birth && <> · *{fmtDateDe(p.date_of_birth)}</>}
                                                    {p.archived && <> · <span className="text-rose-600">{t('patients.list.archived')}</span></>}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="text-brand-text truncate max-w-[220px]">{p.email || <span className="text-brand-text-muted italic">{t('patients.list.noEmail')}</span>}</div>
                                                <div className="text-xs text-brand-text-muted">{p.phone_primary || '—'}</div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="text-brand-text tabular-nums">{p.appointment_count}</div>
                                                {p.last_appointment_date && (
                                                    <div className="text-xs text-brand-text-muted">
                                                        {t('patients.list.last')} {fmtDateDe(p.last_appointment_date)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {p.intake_form_id ? (
                                                    <StatusPill tone="success" dot>{t('patients.list.intakeFilled')}</StatusPill>
                                                ) : (
                                                    <StatusPill tone="neutral" dot>{t('patients.list.intakeOpen')}</StatusPill>
                                                )}
                                                <div className="mt-1 text-[10px] text-brand-text-muted">
                                                    {LANG_META[p.preferred_language] || p.preferred_language}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right">
                                                <Link
                                                    to={`/admin/patients/${p.id}`}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-primary hover:bg-brand-accent/10 rounded transition-colors cursor-pointer"
                                                >
                                                    {t('patients.list.open')}
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <ul className="md:hidden divide-y divide-brand-border">
                            {patients.map((p) => (
                                <li key={p.id}>
                                    <Link to={`/admin/patients/${p.id}`} className="block p-4 hover:bg-brand-surface-2/60 transition-colors">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="font-medium text-brand-text truncate">{p.full_name}</div>
                                                <div className="text-xs text-brand-text-muted truncate">{p.email || p.phone_primary || p.patient_number}</div>
                                            </div>
                                            {p.intake_form_id ? (
                                                <StatusPill tone="success" dot>{t('patients.list.intakeBadge')}</StatusPill>
                                            ) : (
                                                <StatusPill tone="neutral" dot>{t('patients.list.intakeOpen')}</StatusPill>
                                            )}
                                        </div>
                                        <div className="mt-2 flex items-center justify-between text-xs text-brand-text-muted">
                                            <span>{t('patients.list.appointmentsCount', { count: p.appointment_count })}</span>
                                            {p.last_appointment_date && <span>{t('patients.list.last')} {fmtDateDe(p.last_appointment_date)}</span>}
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </div>
    );
};

export default PatientsListPage;
